/* eslint-disable max-lines, max-lines-per-function */
import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDown, ArrowUp, ArrowUpDown, MoreHorizontal } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { deleteJSON, getJSON, patchJSON, postJSON } from '@/lib/api'

const ROLES = [
  'Administrator',
  'Finance Manager',
  'Membership Manager',
  'Event Manager',
  'Communications Manager',
  'View Only',
] as const
const ROLE_CHARTERS: Record<string, string> = {
  Administrator:
    'Everything, plus association settings, the Stripe connection, and seat management',
  'Finance Manager': 'Every money surface — invoices, payments, payouts, QuickBooks, donations',
  'Membership Manager': 'Contacts, memberships, renewals, engagement, and data hygiene',
  'Event Manager': 'Events end to end — tickets, orders, attendees, check-in',
  'Communications Manager': 'Blasts, templates, sending domains, suppressions',
  'View Only': 'Reads every staff surface, writes nothing',
}
type Seat = {
  id: number
  roles: string[]
  staff_roles: string[]
  status: string
  last_active: string | null
  invited_at?: string
  invitation_expires_at?: string
  user: { id: number; name: string; email: string }
}
type SeatsResponse = { seats: Seat[]; administrator_count: number }
type Audit = {
  id: number
  event: string
  details: Record<string, string[]>
  occurred_at: string
  actor: { name: string; email: string }
}

export function SeatsPage({
  viewOnly = false,
  viewerEmail,
}: {
  viewOnly?: boolean
  viewerEmail?: string
}) {
  const client = useQueryClient()
  const [search, setSearch] = React.useState('')
  const [role, setRole] = React.useState<ReadonlySet<string>>(new Set())
  const [pending, setPending] = React.useState<ReadonlySet<string>>(new Set())
  const [sort, setSort] = React.useState<'name' | 'last active'>('name')
  const [direction, setDirection] = React.useState<'ascending' | 'descending'>('ascending')
  const [inviteOpen, setInviteOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<Seat | null>(null)
  const query = useQuery({
    queryKey: ['seats', search, [...role], [...pending], sort, direction],
    queryFn: () =>
      getJSON<SeatsResponse>(
        `/api/v1/seat_management?search=${encodeURIComponent(search)}&role=${encodeURIComponent([...role][0] ?? 'any')}&invite_filter=${pending.size ? 'invites-pending' : 'any'}&sort=${encodeURIComponent(sort)}&direction=${direction}`,
      ),
  })
  const audits = useQuery({
    queryKey: ['seat-audits'],
    queryFn: () => getJSON<{ audits: Audit[] }>('/api/v1/seat_management/audits'),
  })
  const remove = useMutation({
    mutationFn: (id: number) => deleteJSON(`/api/v1/seat_management/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['seats'] }),
  })
  const resend = useMutation({
    mutationFn: (id: number) => postJSON(`/api/v1/staff_invitations/${id}/resend`, {}),
    onSuccess: () => client.invalidateQueries({ queryKey: ['seats'] }),
  })
  const revoke = useMutation({
    mutationFn: (id: number) => deleteJSON(`/api/v1/staff_invitations/${id}`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['seats'] }),
  })
  const toggleSort = (next: 'name' | 'last active') => {
    if (sort === next) setDirection(direction === 'ascending' ? 'descending' : 'ascending')
    else {
      setSort(next)
      setDirection('ascending')
    }
  }
  const rows = query.data?.seats ?? []
  const administratorCount = query.data?.administrator_count ?? 0
  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header className="flex items-start justify-between gap-4 pb-4">
        <div>
          <h1 className="text-heading-sm font-semibold text-foreground">Seats</h1>
          <p className="pt-1 text-body-sm text-muted-foreground">
            Staff access for this association
          </p>
        </div>
        {viewOnly ? (
          <Badge variant="neutral">View only</Badge>
        ) : (
          <Button onClick={() => setInviteOpen(true)}>Invite staff</Button>
        )}
      </header>
      <div className="flex flex-wrap items-center gap-3 pb-4">
        <Input
          className="h-8 w-72"
          placeholder="Search by name or email"
          aria-label="Search by name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FilterDropdown
          label="Role"
          emptyLabel="Role"
          options={ROLES.map((value) => ({ value, label: value }))}
          selected={role}
          onChange={setRole as (value: Set<string>) => void}
        />
        <FilterDropdown
          label="Status"
          emptyLabel="Status"
          options={[{ value: 'invites-pending', label: 'Invite pending' }]}
          selected={pending}
          onChange={setPending as (value: Set<string>) => void}
        />
      </div>
      {query.isLoading ? (
        <p className="text-body-sm text-muted-foreground" role="status">
          Loading seats...
        </p>
      ) : query.isError ? (
        <Alert>Seats are unavailable right now.</Alert>
      ) : (
        <div className="overflow-hidden rounded-md border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <SortHead
                  label="Name"
                  active={sort === 'name'}
                  direction={direction}
                  onClick={() => toggleSort('name')}
                />
                <TableHead>Email</TableHead>
                <TableHead>Roles</TableHead>
                <TableHead>Status</TableHead>
                <SortHead
                  label="Last active"
                  active={sort === 'last active'}
                  direction={direction}
                  onClick={() => toggleSort('last active')}
                />
                <TableHead className="w-12" aria-label="Row actions" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-8 text-center text-body-sm text-muted-foreground"
                  >
                    No seats match these filters.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((seat) => (
                  <SeatRow
                    key={`${seat.status}-${seat.id}`}
                    seat={seat}
                    viewerEmail={viewerEmail}
                    soleAdministrator={
                      seat.status !== 'invite-pending' &&
                      administratorCount === 1 &&
                      seat.staff_roles.includes('Administrator')
                    }
                    viewOnly={viewOnly}
                    onEdit={() => setEditing(seat)}
                    onRemove={() => {
                      if (
                        window.confirm(
                          'Remove this seat? They will lose access to this association, including their browser session and personal access tokens.',
                        )
                      )
                        remove.mutate(seat.id)
                    }}
                    onResend={() => resend.mutate(seat.id)}
                    onRevoke={() => {
                      if (window.confirm('Revoke this invitation?')) revoke.mutate(seat.id)
                    }}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
      <AccessHistory audits={audits.data?.audits ?? []} />
      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onSent={() => client.invalidateQueries({ queryKey: ['seats'] })}
      />
      <EditRolesDialog
        seat={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          client.invalidateQueries({ queryKey: ['seats'] })
          client.invalidateQueries({ queryKey: ['seat-audits'] })
        }}
      />
    </div>
  )
}

function SortHead({
  label,
  active,
  direction,
  onClick,
}: {
  label: string
  active: boolean
  direction: 'ascending' | 'descending'
  onClick: () => void
}) {
  const Icon = active ? (direction === 'ascending' ? ArrowUp : ArrowDown) : ArrowUpDown
  return (
    <TableHead>
      <button
        type="button"
        className="inline-flex items-center gap-1 text-caption font-normal uppercase tracking-wide text-muted-foreground"
        onClick={onClick}
        aria-label={`Sort ${label} ${direction}`}
      >
        <span>{label}</span>
        <Icon className="size-3" aria-hidden="true" />
      </button>
    </TableHead>
  )
}
function SeatRow({
  seat,
  viewerEmail,
  soleAdministrator,
  viewOnly,
  onEdit,
  onRemove,
  onResend,
  onRevoke,
}: {
  seat: Seat
  viewerEmail?: string
  soleAdministrator: boolean
  viewOnly: boolean
  onEdit: () => void
  onRemove: () => void
  onResend: () => void
  onRevoke: () => void
}) {
  const isYou = Boolean(viewerEmail && seat.user.email === viewerEmail)
  const canMenu = !viewOnly && !isYou && !soleAdministrator
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <Avatar className="size-7">
            <AvatarFallback>
              {seat.user.name
                .split(' ')
                .map((part) => part[0])
                .join('')
                .slice(0, 2)
                .toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div>
            <span className="text-body-sm font-medium">{seat.user.name}</span>
            {isYou && <span className="pl-1 text-caption text-muted-foreground">(you)</span>}
          </div>
        </div>
      </TableCell>
      <TableCell className="text-body-sm text-muted-foreground">{seat.user.email}</TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {seat.staff_roles.map((value) => (
            <Badge key={value} variant="neutral">
              {value}
            </Badge>
          ))}
        </div>
      </TableCell>
      <TableCell>
        {seat.status === 'invite-pending' ? (
          <Badge variant="warning">
            Invite pending ·{' '}
            {seat.invitation_expires_at
              ? `${Math.max(0, Math.ceil((new Date(seat.invitation_expires_at).getTime() - Date.now()) / 86400000))} days left`
              : 'expires soon'}
          </Badge>
        ) : (
          <span className="text-body-sm text-muted-foreground">Active</span>
        )}
      </TableCell>
      <TableCell className="text-body-sm text-muted-foreground">
        {seat.last_active ? new Date(seat.last_active).toLocaleString() : '—'}
      </TableCell>
      <TableCell>
        {canMenu && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label={`${seat.user.name} actions`}>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {seat.status === 'invite-pending' ? (
                <>
                  <DropdownMenuItem onSelect={onResend}>Resend invite</DropdownMenuItem>
                  <DropdownMenuItem destructive onSelect={onRevoke}>
                    Revoke invite
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem onSelect={onEdit}>Edit roles</DropdownMenuItem>
                  <DropdownMenuItem destructive onSelect={onRemove}>
                    Remove seat
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </TableCell>
    </TableRow>
  )
}
function AccessHistory({ audits }: { audits: Audit[] }) {
  return (
    <div className="mt-6 border-t border-border pt-4">
      <Collapsible>
        <CollapsibleTrigger className="flex w-full items-center gap-2 py-2 text-left text-body-sm font-medium">
          Access history{' '}
          <span className="text-caption text-muted-foreground">{audits.length} events</span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="divide-y divide-border pl-2">
            {audits.map((audit) => (
              <div key={audit.id} className="py-2 text-caption">
                <span className="text-muted-foreground">
                  {new Date(audit.occurred_at).toLocaleString()}
                </span>{' '}
                · {audit.actor.name} ·{' '}
                {audit.event === 'role_changed'
                  ? `${audit.details.old_roles?.join(' + ')} → ${audit.details.new_roles?.join(' + ')}`
                  : audit.event.replaceAll('_', ' ')}
              </div>
            ))}
          </div>
          <p className="pt-2 text-caption text-muted-foreground">
            History is permanent and visible to Administrators and View Only.
          </p>
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}
function InviteDialog({
  open,
  onOpenChange,
  onSent,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSent: () => void
}) {
  const [email, setEmail] = React.useState('')
  const [roles, setRoles] = React.useState<string[]>([])
  const [error, setError] = React.useState<string | null>(null)
  const mutation = useMutation({
    mutationFn: () =>
      postJSON('/api/v1/staff_invitations', {
        invitation: { email, roles, idempotency_key: crypto.randomUUID() },
      }),
    onSuccess: () => {
      onSent()
      onOpenChange(false)
      setEmail('')
      setRoles([])
      setError(null)
    },
    onError: (e: Error) => setError(e.message),
  })
  const toggle = (role: string) =>
    setRoles((current) =>
      current.includes(role) ? current.filter((value) => value !== role) : [...current, role],
    )
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite staff</DialogTitle>
          <DialogDescription>They'll get an email link that expires in 7 days.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.org"
              aria-invalid={Boolean(error)}
            />
            {error && <p className="text-caption text-destructive">{error}</p>}
          </div>
          <div className="space-y-2">
            <span className="text-body-sm font-medium">Roles</span>
            {ROLES.map((role) => (
              <div key={role} className="flex items-start gap-2">
                <Checkbox
                  id={`invite-role-${role}`}
                  checked={roles.includes(role)}
                  onCheckedChange={() => toggle(role)}
                />
                <Label htmlFor={`invite-role-${role}`} className="flex-1 cursor-pointer">
                  <span className="block text-body-sm font-medium">{role}</span>
                  <span className="block text-caption text-muted-foreground">
                    {ROLE_CHARTERS[role]}
                  </span>
                </Label>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!email || roles.length === 0 || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Send invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
function EditRolesDialog({
  seat,
  onClose,
  onSaved,
}: {
  seat: Seat | null
  onClose: () => void
  onSaved: () => void
}) {
  const [roles, setRoles] = React.useState<string[]>([])
  React.useEffect(() => setRoles(seat?.staff_roles ?? []), [seat])
  const mutation = useMutation({
    mutationFn: () => patchJSON(`/api/v1/seat_management/${seat?.id}`, { seat: { roles } }),
    onSuccess: onSaved,
  })
  return (
    <Dialog open={Boolean(seat)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit roles</DialogTitle>
          <DialogDescription>Choose the roles this staffer can use.</DialogDescription>
        </DialogHeader>
        {ROLES.map((role) => (
          <div key={role} className="flex items-start gap-2">
            <Checkbox
              id={`edit-role-${role}`}
              checked={roles.includes(role)}
              onCheckedChange={() =>
                setRoles((current) =>
                  current.includes(role)
                    ? current.filter((value) => value !== role)
                    : [...current, role],
                )
              }
            />
            <Label htmlFor={`edit-role-${role}`} className="flex-1 cursor-pointer">
              <span className="block text-body-sm font-medium">{role}</span>
              <span className="block text-caption text-muted-foreground">
                {ROLE_CHARTERS[role]}
              </span>
            </Label>
          </div>
        ))}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={roles.length === 0 || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Save roles
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
